import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, ColorPicker, Input, InputNumber, Popconfirm, Radio, Select, Tooltip } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  ArrowDownOutlined, ArrowUpOutlined, CopyOutlined, DeleteOutlined, PlusOutlined, RightOutlined,
} from '@ant-design/icons';
import { toggleBold } from '../toggleBold';
import { FieldSpec, uniqueId } from './cardTypes';
import { cloneJson, insertAt, moveItem, removeAt, replaceAt } from './listOps';

type InputStatus = 'warning' | undefined;

/** Plain one-line preview of lesson copy: drops **bold** markers and collapses whitespace. */
export function plainText(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim() : '';
}

export function isBlank(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && !value.trim());
}

// ─── Building blocks ───

interface FieldProps {
  label?: React.ReactNode;
  required?: boolean;
  help?: React.ReactNode;
  children: React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({ label, required, help, children }) => (
  <div className="lbe-field">
    {label && (
      <span className="lbe-label">
        {label}
        {required && <span className="lbe-label-required">*</span>}
      </span>
    )}
    {children}
    {help && <span className="lbe-help">{help}</span>}
  </div>
);

export const Section: React.FC<{ title: React.ReactNode; help?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }> = ({
  title, help, extra, children,
}) => (
  <div className="lbe-section">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <div className="lbe-section-title">{title}</div>
      {extra}
    </div>
    {help && <div className="lbe-section-help">{help}</div>}
    {children}
  </div>
);

interface RichTextAreaProps {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  status?: InputStatus;
}

/** Multi-line input for lesson copy. Ctrl/⌘+B toggles **bold** around the selection, as in the JSON editor. */
export const RichTextArea: React.FC<RichTextAreaProps> = ({ value, onChange, placeholder, minRows = 2, status }) => {
  const ref = useRef<TextAreaRef>(null);
  const pendingSelection = useRef<[number, number] | null>(null);

  // Restore the selection after the toggled text comes back through `value`.
  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    const el = ref.current?.resizableTextArea?.textArea;
    if (!selection || !el) return;
    pendingSelection.current = null;
    el.setSelectionRange(selection[0], selection[1]);
  }, [value]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // `code`, not `key`: with a Cyrillic layout the B key reports "и".
    if (!(e.metaKey || e.ctrlKey) || e.code !== 'KeyB') return;
    e.preventDefault();
    const el = e.currentTarget;
    const result = toggleBold(el.value, el.selectionStart, el.selectionEnd);
    pendingSelection.current = [result.selFrom, result.selTo];
    onChange(result.text);
  };

  return (
    <Input.TextArea
      ref={ref}
      value={value ?? ''}
      placeholder={placeholder}
      status={status}
      autoSize={{ minRows, maxRows: 14 }}
      onKeyDown={onKeyDown}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export const ColorInput: React.FC<{ value?: string | null; onChange: (value: string) => void; status?: InputStatus }> = ({
  value, onChange, status,
}) => (
  <div className="lbe-color">
    <ColorPicker
      value={value && HEX_COLOR.test(value) ? value : null}
      onChangeComplete={(c) => onChange(c.toHexString().toUpperCase())}
    />
    <Input value={value ?? ''} placeholder="#60A5FA" status={status} onChange={(e) => onChange(e.target.value)} />
  </div>
);

interface ItemActionsProps {
  index: number;
  count: number;
  onMove: (to: number) => void;
  onRemove: () => void;
  onDuplicate?: () => void;
  /** Ask before deleting — for items that hold a lot of content. */
  confirmRemove?: string;
}

export const ItemActions: React.FC<ItemActionsProps> = ({ index, count, onMove, onRemove, onDuplicate, confirmRemove }) => {
  const removeButton = <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={confirmRemove ? undefined : onRemove} />;
  return (
    // Item headers toggle collapse on click; the actions must not.
    <span style={{ display: 'inline-flex', flex: 'none' }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Move up">
        <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove(index - 1)} />
      </Tooltip>
      <Tooltip title="Move down">
        <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={index === count - 1} onClick={() => onMove(index + 1)} />
      </Tooltip>
      {onDuplicate && (
        <Tooltip title="Duplicate">
          <Button type="text" size="small" icon={<CopyOutlined />} onClick={onDuplicate} />
        </Tooltip>
      )}
      {confirmRemove ? (
        <Popconfirm title={confirmRemove} okText="Delete" okButtonProps={{ danger: true }} onConfirm={onRemove}>
          {removeButton}
        </Popconfirm>
      ) : (
        <Tooltip title="Delete">{removeButton}</Tooltip>
      )}
    </span>
  );
};

export const AddButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <div>
    <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={onClick}>{children}</Button>
  </div>
);

// ─── Schema-driven fields ───

/** Sets `key`, or drops it when a field the apps treat as optional is cleared. */
function withField(source: Record<string, any>, key: string, next: unknown, optional: boolean): Record<string, any> {
  const empty = next === undefined || next === null || next === '' || (Array.isArray(next) && next.length === 0);
  if (empty && optional) {
    const { [key]: _removed, ...rest } = source;
    return rest;
  }
  return { ...source, [key]: next };
}

function firstTextKey(fields: FieldSpec[]): string | undefined {
  for (const field of fields) {
    if (field.kind === 'row') {
      const key = firstTextKey(field.fields);
      if (key) return key;
    } else if (field.kind === 'text' || field.kind === 'richtext') {
      return field.key;
    }
  }
  return undefined;
}

type StringListSpec = Extract<FieldSpec, { kind: 'stringList' }>;
type ObjectListSpec = Extract<FieldSpec, { kind: 'objectList' }>;

const StringListField: React.FC<{ field: StringListSpec; items: string[]; onChange: (items: string[]) => void }> = ({
  field, items, onChange,
}) => (
  <div className="lbe-items">
    {items.map((item, i) => (
      <div className="lbe-list-row" key={i}>
        <span className="lbe-list-num">{i + 1}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {field.rich ? (
            <RichTextArea minRows={1} value={item} onChange={(v) => onChange(replaceAt(items, i, v))} />
          ) : (
            <Input value={item ?? ''} onChange={(e) => onChange(replaceAt(items, i, e.target.value))} />
          )}
        </div>
        <ItemActions
          index={i}
          count={items.length}
          onMove={(to) => onChange(moveItem(items, i, to))}
          onRemove={() => onChange(removeAt(items, i))}
        />
      </div>
    ))}
    <AddButton onClick={() => onChange([...items, ''])}>Add {field.itemLabel}</AddButton>
  </div>
);

const ObjectListField: React.FC<{ field: ObjectListSpec; items: Record<string, any>[]; onChange: (items: Record<string, any>[]) => void }> = ({
  field, items, onChange,
}) => {
  // Collapse flags are positional, so they move with their items.
  const [collapsed, setCollapsed] = useState<boolean[]>([]);
  const flags = () => items.map((_, i) => !!collapsed[i]);
  const summaryKey = firstTextKey(field.fields);
  const noun = field.itemLabel.toLowerCase();

  const withFreshId = (item: Record<string, any>) => {
    if (!field.idKey) return item;
    const ids = items.map((it) => String(it?.[field.idKey!] ?? ''));
    return { ...item, [field.idKey]: uniqueId(ids, field.idPrefix ?? 'id') };
  };

  const move = (from: number, to: number) => {
    onChange(moveItem(items, from, to));
    setCollapsed(moveItem(flags(), from, to));
  };
  const remove = (index: number) => {
    onChange(removeAt(items, index));
    setCollapsed(removeAt(flags(), index));
  };
  const duplicate = (index: number) => {
    onChange(insertAt(items, index + 1, withFreshId(cloneJson(items[index]))));
    setCollapsed(insertAt(flags(), index + 1, false));
  };
  const toggle = (index: number) => setCollapsed(replaceAt(flags(), index, !collapsed[index]));

  return (
    <div className="lbe-items">
      {items.map((item, i) => {
        const isCollapsed = !!collapsed[i];
        return (
          <div key={i} className={`lbe-item${isCollapsed ? ' is-collapsed' : ''}`}>
            <div className="lbe-item-head" onClick={() => toggle(i)}>
              <RightOutlined rotate={isCollapsed ? 0 : 90} style={{ fontSize: 10, color: '#8c8c8c' }} />
              <span className="lbe-item-title">{field.itemLabel} {i + 1}</span>
              <span className="lbe-item-summary">{summaryKey ? plainText(item?.[summaryKey]) : ''}</span>
              <ItemActions
                index={i}
                count={items.length}
                onMove={(to) => move(i, to)}
                onDuplicate={() => duplicate(i)}
                onRemove={() => remove(i)}
                confirmRemove={`Delete ${noun} ${i + 1}?`}
              />
            </div>
            {!isCollapsed && (
              <div className="lbe-item-body">
                <SchemaFields fields={field.fields} value={item ?? {}} onChange={(next) => onChange(replaceAt(items, i, next))} />
              </div>
            )}
          </div>
        );
      })}
      <AddButton onClick={() => onChange([...items, withFreshId(field.create())])}>Add {noun}</AddButton>
    </div>
  );
};

interface FieldControlProps {
  field: FieldSpec;
  value: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}

const FieldControl: React.FC<FieldControlProps> = ({ field, value, onChange }) => {
  if (field.kind === 'row') {
    return (
      <div className="lbe-row" style={{ gridTemplateColumns: field.columns ?? `repeat(${field.fields.length}, minmax(0, 1fr))` }}>
        {field.fields.map((child, i) => (
          <FieldControl key={child.kind === 'row' ? `row-${i}` : child.key} field={child} value={value} onChange={onChange} />
        ))}
      </div>
    );
  }

  const current = value[field.key];
  const set = (next: unknown) => onChange(withField(value, field.key, next, !!field.optional));
  const status: InputStatus = field.required && isBlank(current) ? 'warning' : undefined;

  let control: React.ReactNode;
  switch (field.kind) {
    case 'text':
      control = <Input value={current ?? ''} placeholder={field.placeholder} status={status} onChange={(e) => set(e.target.value)} />;
      break;
    case 'emoji':
      control = (
        <Input
          value={current ?? ''}
          placeholder={field.placeholder ?? '🙂'}
          status={status}
          style={{ textAlign: 'center' }}
          onChange={(e) => set(e.target.value)}
        />
      );
      break;
    case 'richtext':
      control = <RichTextArea value={current} placeholder={field.placeholder} status={status} onChange={set} />;
      break;
    case 'color':
      control = <ColorInput value={current} status={status} onChange={set} />;
      break;
    case 'number':
      control = (
        <InputNumber<number>
          value={current ?? null}
          min={field.min}
          suffix={field.suffix}
          status={status}
          style={{ width: 160 }}
          onChange={set}
        />
      );
      break;
    case 'boolean':
      control = (
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          size="small"
          value={!!current}
          options={[{ label: field.on, value: true }, { label: field.off, value: false }]}
          onChange={(e) => set(e.target.value)}
        />
      );
      break;
    case 'select':
      control = (
        <Select
          value={current}
          allowClear={!field.required}
          style={{ minWidth: 160 }}
          options={field.options.map((o) => ({ value: o.value, label: o.label }))}
          onChange={set}
        />
      );
      break;
    case 'stringList':
      control = <StringListField field={field} items={Array.isArray(current) ? current : []} onChange={set} />;
      break;
    case 'objectList':
      control = <ObjectListField field={field} items={Array.isArray(current) ? current : []} onChange={set} />;
      break;
    case 'object':
      control = (
        <div className={`lbe-group${field.tone ? ` tone-${field.tone}` : ''}`}>
          <SchemaFields fields={field.fields} value={current ?? {}} onChange={set} />
        </div>
      );
      break;
  }

  return (
    <Field label={field.label} required={field.required} help={field.help}>
      {control}
    </Field>
  );
};

interface SchemaFieldsProps {
  fields: FieldSpec[];
  value: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}

/** Renders a field schema (see cardTypes.ts) as a form bound to `value`. */
export const SchemaFields: React.FC<SchemaFieldsProps> = ({ fields, value, onChange }) => (
  <div className="lbe-fields">
    {fields.map((field, i) => (
      <FieldControl key={field.kind === 'row' ? `row-${i}` : field.key} field={field} value={value} onChange={onChange} />
    ))}
  </div>
);
